import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { useContent } from 'context/ContentContext';
import { useCloverContext } from 'context/CloverContext';
import { get } from 'firebase/database';
import { dbRef } from 'utils/dbRef';
import { useSiteInfo } from 'hooks/useSiteInfo';
import { getNextOpeningTime, isInGracePeriod } from 'utils/businessHours';
import PageSection from 'components/ui/PageSection';
import MenuItem from 'components/menu/MenuItem';
import Button from 'components/ui/Button';
import MenuCategorySelector from 'components/menu/CategorySelector';
import ClosedBanner from 'components/ui/ClosedBanner';
import {
  resolveCategoryIdFromSlug,
  resolveInitialCategoryId,
} from 'components/menu/categoryDeepLink';
import { scrollSectionIntoView } from 'utils/scroll';

const SectionHeading = styled.h2`
  ${({ theme }) => theme.mixins.textH2};
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  margin-top: 3rem;
  margin-bottom: 1rem;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-top: 3rem;
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled.p`
  ${({ theme }) => theme.mixins.textP1};
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
  margin: 0 auto 20px;

  ${({ theme }) => theme.mediaQueries.md} {
    margin: 0 auto 40px;
  }
`;

const CategoryButtonsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    display: none;
  }

  button {
    white-space: nowrap;
    flex-shrink: 0;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    flex-wrap: wrap;
    justify-content: center;
    overflow-x: visible;
    gap: 20px;
    margin-bottom: 40px;
    padding-bottom: 0;

    button {
      white-space: normal;
      flex-shrink: 1;
    }
  }
`;

const MenuItemsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.md} {
    gap: 1.5rem;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    max-width: ${({ theme }) => theme.layouts.maxWidth};
  }

  /* Stagger animation for menu items - only when shopping is enabled */
  > * {
    animation: ${({ shoppingEnabled }) =>
      shoppingEnabled ? 'fadeInUp 0.4s ease-out forwards' : 'none'};
    opacity: ${({ shoppingEnabled }) => (shoppingEnabled ? '0' : '1')};
  }

  ${({ shoppingEnabled }) =>
    shoppingEnabled &&
    `
    > *:nth-of-type(1) {
      animation-delay: 0.05s;
    }
    > *:nth-of-type(2) {
      animation-delay: 0.1s;
    }
    > *:nth-of-type(3) {
      animation-delay: 0.15s;
    }
    > *:nth-of-type(4) {
      animation-delay: 0.2s;
    }
    > *:nth-of-type(n + 5) {
      animation-delay: 0.25s;
    }
  `}

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ViewMoreContainer = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  margin-top: 1rem;
  margin-bottom: 2rem;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-top: 1.5rem;
    margin-bottom: 3rem;
  }

  button {
    width: 100%;

    ${({ theme }) => theme.mediaQueries.md} {
      width: auto;
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.darkGrey};
  text-align: center;
`;

const MenuBackground = styled.div`
  width: 100%;
  background-color: #ffe2b1;
  background-image: url('https://storage.googleapis.com/birreria-el-semaforo/images/background/l.webp');
  background-repeat: repeat;
  background-size: 700px;
  background-blend-mode: multiply;
`;

const ErrorMessage = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  max-width: ${({ theme }) => theme.layouts.maxWidth};
  margin: 0 auto 32px;
`;

const Menu = ({ deepLinkCategoryId = null, deepLinkCategorySlug = null }) => {
  const { menu = {}, siteInfo = {}, messages = {}, language } = useContent();
  const {
    menuData,
    loading,
    error,
    useLocalData,
    refreshMenu,
    remoteMenuEnabled,
  } = useCloverContext();

  // Get site info for business hours checking
  const {
    isClosed,
    businessHours,
    businessHoursOverride,
    gracePeriodMinutes,
    timezone,
    rawShoppingEnabled,
    orderingEnabled,
  } = useSiteInfo();

  // State for pagination
  const [visibleItems, setVisibleItems] = useState(4);
  const [activeCategory, setActiveCategory] = useState('');

  // A deep link (/liquor) gets to pick the category exactly once. After that the
  // visitor owns the selection, and a later recovery — the category being
  // deleted from Clover mid-session — must fall back to the normal default
  // rather than yanking them back to where the link pointed.
  const deepLinkAppliedRef = useRef(false);
  const pendingDeepLinkScrollRef = useRef(
    Boolean(deepLinkCategorySlug || deepLinkCategoryId),
  );

  const { title, subtitle, buttonText } = menu || {};

  // Use Clover data if available, otherwise fall back to content data
  const menuCategories = useMemo(() => {
    return Array.isArray(menuData) ? menuData : menuData?.categories || [];
  }, [menuData]);

  // Determine which data source to use
  const useCloverData = menuCategories.length > 0;

  // Filter to only show categories where showOnWeb is true - memoized
  const categories = useMemo(
    () =>
      menuCategories.filter(
        (category) =>
          category &&
          category.id &&
          (category.showOnWeb === true || category.showOnWeb === undefined),
      ),
    [menuCategories],
  );

  // Check if we have any items in the categories
  const hasItems =
    useCloverData &&
    categories.some((cat) => cat.items && cat.items.length > 0);

  // Re-arm when the deep-link route changes without unmounting Menu. `/` and
  // `/liquor` both render <Home>, so navigating between them updates this
  // component rather than remounting it; without this, a second visit to
  // /liquor would keep the previous selection and never scroll.
  useEffect(() => {
    if (!deepLinkCategorySlug && !deepLinkCategoryId) return;

    deepLinkAppliedRef.current = false;
    pendingDeepLinkScrollRef.current = true;
    // No-op on first mount; on a client-side nav this forces the resolver below
    // to run again instead of keeping a still-valid previous selection.
    setActiveCategory('');
    setVisibleItems(4);
  }, [deepLinkCategoryId, deepLinkCategorySlug]);

  // The single writer for the initial (and recovery) category selection.
  //
  // This deliberately replaces what used to be two competing writers: a deep
  // link cannot simply set the category first, because the default-select pass
  // awaits an RTDB read and its continuation would land afterwards and overwrite
  // the choice. One effect, one decision, delegated to a pure function.
  useEffect(() => {
    if (categories.length === 0) return;

    // A valid selection is never overridden — this is what keeps a visitor's tab
    // click from being undone when menuData re-emits.
    if (activeCategory && categories.some((cat) => cat.id === activeCategory)) {
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      let categoryOrder = null;

      try {
        const categoryOrderSnapshot = await get(
          dbRef('siteInfo/categoryOrder'),
        );
        categoryOrder = categoryOrderSnapshot.val();
      } catch (error) {
        // Non-fatal: without an explicit order we still fall back to a sensible
        // default below.
        Sentry.captureException(error, {
          tags: { component: 'Menu', action: 'fetchCategoryOrder' },
        });
      }

      if (cancelled) return;

      const applyingDeepLink = !deepLinkAppliedRef.current;
      const nextId = resolveInitialCategoryId({
        categories,
        categoryOrder,
        deepLinkCategoryId: applyingDeepLink ? deepLinkCategoryId : null,
        deepLinkSlug: applyingDeepLink ? deepLinkCategorySlug : null,
      });

      // Surface a deep link that silently stopped working. Renaming or hiding
      // a category in Clover is a normal business action with no obvious
      // connection to a printed URL, so without this the link quietly degrades
      // to "lands on the default category" and nobody finds out.
      //
      // The match is checked against resolveCategoryIdFromSlug, not the
      // resolver above: that one always falls back to a default, so comparing
      // against it would make every miss look like a hit.
      const deepLinkRequested = Boolean(
        deepLinkCategoryId || deepLinkCategorySlug,
      );
      const deepLinkMatched =
        (deepLinkCategoryId && nextId === deepLinkCategoryId) ||
        (deepLinkCategorySlug &&
          nextId ===
            resolveCategoryIdFromSlug(categories, deepLinkCategorySlug));

      if (applyingDeepLink && deepLinkRequested && !deepLinkMatched) {
        Sentry.captureMessage(
          'Menu deep link matched no visible category; fell back to the default',
          {
            level: 'warning',
            tags: { component: 'Menu', action: 'resolveDeepLinkCategory' },
            extra: {
              deepLinkCategoryId,
              deepLinkCategorySlug,
              visibleCategories: categories.map((cat) => cat.name),
            },
          },
        );
      }

      deepLinkAppliedRef.current = true;

      if (nextId) {
        setActiveCategory(nextId);
        setVisibleItems(4);
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [categories, activeCategory, deepLinkCategoryId, deepLinkCategorySlug]);

  // Scroll to the menu once a category is actually mounted, so the visitor does
  // not land on an empty panel mid-fetch.
  //
  // Stays armed even when the slug resolved to nothing: someone who followed a
  // link to the liquor menu should still end up at the menu, just on the default
  // category. The flag is consumed inside the frame callback rather than before
  // it, so StrictMode's double-invoke (which cancels the first frame) re-arms
  // instead of swallowing the scroll.
  useEffect(() => {
    if (!pendingDeepLinkScrollRef.current || !activeCategory) return;

    const frame = requestAnimationFrame(() => {
      pendingDeepLinkScrollRef.current = false;
      scrollSectionIntoView('menu');
    });

    return () => cancelAnimationFrame(frame);
  }, [activeCategory]);

  // Convert Clover items to the format expected by MenuItem component
  const convertCloverItems = (items = []) => {
    return items
      .filter((item) => item.showOnWeb !== false)
      .map((item) => {
        // Construct modifierGroups - keep prices as numbers
        const modifierGroups =
          item.modifierGroups && item.modifierGroups.length > 0
            ? item.modifierGroups
                .map((group) => ({
                  id: group.id,
                  name: group.name,
                  required: group.minRequired > 0,
                  minRequired: group.minRequired,
                  maxAllowed: group.maxAllowed,
                  selectionType: group.maxAllowed === 1 ? 'single' : 'multiple',
                  modifiers: group.modifiers
                    .filter(
                      (mod) => mod.name.toUpperCase() !== 'TOGO', // Filter out TOGO modifier
                    )
                    .map((mod) => ({
                      id: mod.id,
                      name: mod.name,
                      description: mod.description,
                      price: mod.price || 0, // Keep as number!
                      imageUrl: mod.imageUrl,
                      available: mod.available !== false,
                    })),
                }))
                .filter((group) => group.modifiers.length > 0) // Remove empty groups
            : null;

        // Extract add-ons from Clover item if available
        const addOns = [];
        if (item.addOnGroups && item.addOnGroups.length > 0) {
          item.addOnGroups.forEach((group) => {
            if (group.addOns && group.addOns.length > 0) {
              group.addOns.forEach((addOn) => {
                addOns.push({
                  id: addOn.id,
                  name: addOn.name,
                  price: addOn.price || 0, // Keep as number!
                });
              });
            }
          });
        }

        return {
          id: item.id,
          name: item.name,
          description: item.description || '',
          price: item.price || 0, // Keep as number (cents)!
          modifierGroups: modifierGroups,
          addOns: addOns.length > 0 ? addOns : null,
          imageUrl: item.imageUrl || null,
          isSoldOnline: item.isSoldOnline,
        };
      });
  };

  // Normalize static menu data to match the expected structure
  const normalizeStaticMenuItems = (items = []) => {
    return items
      .filter((item) => item.showOnWeb !== false) // Filter items that should be shown on web
      .map((item) => {
        // Ensure item has all required properties
        return {
          ...item,
          // Ensure price is a number in cents
          price:
            typeof item.price === 'string'
              ? parseFloat(item.price.replace(/[^0-9.]/g, '')) * 100
              : item.price,
          // Ensure modifiers are in the correct format
          modifiers: item.modifiers
            ? Array.isArray(item.modifiers) &&
              item.modifiers.length > 0 &&
              typeof item.modifiers[0] === 'object'
              ? item.modifiers.filter(
                  (mod) => (mod.name || '').toUpperCase() !== 'TOGO', // Filter out TOGO modifier
                )
              : // Convert string array to object array with id and name
                item.modifiers
                  .filter((mod) => mod.toUpperCase() !== 'TOGO') // Filter out TOGO modifier
                  .map((mod, index) => ({
                    id: `mod-${index}`,
                    name: mod,
                  }))
            : null,
          // Ensure add-ons are in the correct format
          addOns: item.addOns
            ? Array.isArray(item.addOns) &&
              item.addOns.length > 0 &&
              typeof item.addOns[0] === 'object'
              ? item.addOns
              : // Convert object to array of objects with id, name, and price
                Object.entries(item.addOns).map(([key, value]) => ({
                  id: key,
                  name:
                    key.charAt(0).toUpperCase() +
                    key.slice(1).replace(/([A-Z])/g, ' $1'),
                  price: value,
                }))
            : null,
        };
      });
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setVisibleItems(4);
  };

  // Handle order button click
  const handleOrderButtonClick = () => {
    // Handle online ordering, could open cart drawer or navigate to ordering page
    if (typeof window !== 'undefined' && siteInfo?.orderUrl) {
      window.open(siteInfo.orderUrl, '_blank');
    }
  };

  // Early returns after hooks
  if (!useLocalData && (!menuData || typeof menuData !== 'object')) {
    return (
      <PageSection id="menu" aria-labelledby="menu-heading">
        <SectionHeading id="menu-heading">{title}</SectionHeading>
        <Subtitle>{subtitle}</Subtitle>
        <LoadingState>
          {messages.loading?.menuData || 'Loading menu data...'}
        </LoadingState>
      </PageSection>
    );
  }

  if (!useLocalData && (!menuData || !Array.isArray(menuData.categories))) {
    return (
      <PageSection id="menu" aria-labelledby="menu-heading">
        <SectionHeading id="menu-heading">{title}</SectionHeading>
        <Subtitle>{subtitle}</Subtitle>
        <LoadingState>
          {messages.loading?.menuData || 'Loading menu data...'}
        </LoadingState>
      </PageSection>
    );
  }

  // If loading and no categories yet, show loading state
  if (loading && categories.length === 0) {
    return (
      <PageSection id="menu" aria-labelledby="menu-heading">
        <SectionHeading id="menu-heading">{title}</SectionHeading>
        <Subtitle>{subtitle}</Subtitle>
        <LoadingState>
          {messages.loading?.menuItems || 'Loading menu items...'}
        </LoadingState>
      </PageSection>
    );
  }

  // If no categories to show (all were filtered out), show appropriate message
  if (categories.length === 0) {
    return (
      <PageSection id="menu" aria-labelledby="menu-heading">
        <SectionHeading id="menu-heading">{title}</SectionHeading>
        <Subtitle>{subtitle}</Subtitle>
        <LoadingState>
          {messages.noMenuCategories ||
            'No menu categories available for display.'}
        </LoadingState>
      </PageSection>
    );
  }

  // Calculate next opening time for banner
  const nextOpening =
    isClosed && businessHours
      ? getNextOpeningTime(
          businessHours,
          businessHoursOverride,
          new Date(),
          language,
        )
      : null;

  // Check if we're in grace period
  const inGracePeriod =
    businessHours && !businessHoursOverride
      ? isInGracePeriod(
          businessHours,
          businessHoursOverride,
          new Date(),
          gracePeriodMinutes,
          timezone,
        )
      : false;

  return (
    <MenuBackground>
      <PageSection id="menu" aria-labelledby="menu-heading">
        <SectionHeading id="menu-heading">{title}</SectionHeading>
        <Subtitle>{subtitle}</Subtitle>

        <ClosedBanner
          isClosed={isClosed}
          nextOpening={nextOpening}
          businessHoursOverride={businessHoursOverride}
          isGracePeriod={inGracePeriod}
          shoppingEnabled={rawShoppingEnabled}
          orderingEnabled={orderingEnabled}
          businessHours={businessHours}
        />

        {remoteMenuEnabled && useCloverData && useLocalData && (
          <ErrorMessage>
            Using cached menu data which may not be up to date.
            <Button size="small" variant="primary" onClick={refreshMenu}>
              Refresh
            </Button>
          </ErrorMessage>
        )}

        {error && !useLocalData && (
          <ErrorMessage>
            There was an issue loading the menu.
            <Button size="small" variant="primary" onClick={refreshMenu}>
              Try Again
            </Button>
          </ErrorMessage>
        )}

        {useCloverData &&
          !hasItems &&
          (menuData?.allItems || []).length > 0 && (
            <ErrorMessage>
              Menu items could not be properly categorized.
              <Button size="small" variant="primary" onClick={refreshMenu}>
                Retry
              </Button>
            </ErrorMessage>
          )}

        <CategoryButtonsContainer role="tablist" aria-label="Menu categories">
          <MenuCategorySelector
            categories={categories}
            activeCategory={activeCategory}
            handleCategoryChange={handleCategoryChange}
          />
        </CategoryButtonsContainer>

        {categories.map((category) => {
          // Get items based on data source
          let categoryItems = [];

          if (useCloverData) {
            const cloverCategoryItems = category.items || [];
            categoryItems = convertCloverItems(cloverCategoryItems);
          } else {
            const staticItems = menu?.items?.[category.id] || [];
            categoryItems = normalizeStaticMenuItems(staticItems);
          }

          return (
            <div
              key={category.id}
              role="tabpanel"
              id={`panel-${category.id}`}
              aria-labelledby={`tab-${category.id}`}
              hidden={activeCategory !== category.id}
            >
              {activeCategory === category.id && (
                <MenuItemsGrid shoppingEnabled={rawShoppingEnabled}>
                  {categoryItems.slice(0, visibleItems).map((item, index) => (
                    <MenuItem
                      key={item.id || `${category.id}-item-${index}`}
                      item={item}
                      categoryId={category.id}
                      index={index}
                    />
                  ))}
                  {categoryItems.length > visibleItems && (
                    <ViewMoreContainer>
                      <Button
                        size="large"
                        variant="primary"
                        onClick={() => setVisibleItems(categoryItems.length)}
                      >
                        {messages?.viewMore || 'View More'}
                      </Button>
                    </ViewMoreContainer>
                  )}
                </MenuItemsGrid>
              )}
            </div>
          );
        })}

        {/* Fallback view for all items if categorization failed */}
        {useCloverData &&
          !hasItems &&
          (menuData?.allItems || []).length > 0 &&
          activeCategory === categories[0]?.id && (
            <MenuItemsGrid shoppingEnabled={rawShoppingEnabled}>
              {convertCloverItems(menuData.allItems).map((item, index) => (
                <MenuItem
                  key={item.id || `all-items-${index}`}
                  item={item}
                  categoryId={categories[0]?.id}
                  index={index}
                />
              ))}
            </MenuItemsGrid>
          )}

        {/* Order button */}
        {buttonText && siteInfo?.orderUrl && (
          <Button
            size="large"
            variant="secondary"
            onClick={handleOrderButtonClick}
          >
            {buttonText}
            {siteInfo?.orderButtonIcon && (
              <span className="icon" aria-hidden="true">
                {siteInfo.orderButtonIcon}
              </span>
            )}
          </Button>
        )}
      </PageSection>
    </MenuBackground>
  );
};

Menu.propTypes = {
  /** Exact Clover category id from config/menuDeepLinks.js, when known. */
  deepLinkCategoryId: PropTypes.string,
  /** Human-readable category token from the route, e.g. 'liquor'. */
  deepLinkCategorySlug: PropTypes.string,
};

export default Menu;
